import { useTar } from '@/modules'
import { Logo } from '../Misc/Logo'

export const AboutPage = () => {
    const { t } = useTar()
    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 max-w-6xl space-y-12 mt-8 mb-16">
                {/* Header Section */}
                <div className="text-center space-y-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                        About <Logo />
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Building practical experience for data professionals
                    </p>
                </div>

                {/* Mission Section */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-8 space-y-6">
                    <div className="space-y-4">
                        <h2 className="text-3xl font-bold text-foreground">Our Mission</h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            We believe that the gap between education and employment is real—and frustrating.
                            Too many talented people are told they "need more experience" but are never given the chance to gain it.
                        </p>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            <strong className="text-foreground">XP Lab</strong> is here to change that. We provide a safe, structured environment where
                            data professionals can practice real-world challenges, receive honest feedback, and build
                            the confidence they need to succeed in their careers, whilst actually improving.
                        </p>
                    </div>
                </div>

                {/* What We Offer Section */}
                <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-foreground text-center">What We Offer</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-card rounded-lg shadow-sm border border-border p-6 space-y-3">
                            <div className="text-4xl">🎯</div>
                            <h3 className="text-xl font-semibold text-foreground">Real-World Challenges</h3>
                            <p className="text-muted-foreground">
                                No textbook exercises.
                                No query overload.
                                Work on realistic scenarios that mirror what you'll face
                                in actual data roles.
                            </p>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-6 space-y-3">
                            <div className="text-4xl">💬</div>
                            <h3 className="text-xl font-semibold text-foreground">Actionable Feedback</h3>
                            <p className="text-muted-foreground">
                                Get detailed, constructive feedback on your work—both AI-powered and human-reviewed—to
                                help you grow faster, and in the right direction.
                            </p>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-6 space-y-3">
                            <div className="text-4xl">📈</div>
                            <h3 className="text-xl font-semibold text-foreground">Track Your Progress</h3>
                            <p className="text-muted-foreground">
                                Monitor your growth over time, learn how to use tools like SQL to get to the answer, and see
                                how far you've come.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Philosophy Section */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-8 space-y-6">
                    <div className="space-y-4">
                        <h2 className="text-3xl font-bold text-foreground">Our Philosophy</h2>
                        <div className="space-y-4">
                            <div className="flex gap-4">
                                <div className="text-2xl mt-1">🧠</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-foreground mb-2">Learning by Doing</h3>
                                    <p className="text-muted-foreground">
                                        We don't teach from slides or tutorials. You learn by solving problems, making mistakes,
                                        and iterating—just like in a real job.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="text-2xl mt-1">⏳</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-foreground mb-2">Deep Learning, Not Quick Fixes</h3>
                                    <p className="text-muted-foreground">
                                        Our cooldown system encourages reflection and internalization. We care about
                                        lasting improvement, not just quick retries.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="text-2xl mt-1">🤝</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-foreground mb-2">Radical Candor</h3>
                                    <p className="text-muted-foreground">
                                        We give you real feedback designed to help you improve, both on the job and on getting a job.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Team Section */}
                <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-foreground text-center">The Team</h2>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-8">
                        <p className="text-lg text-muted-foreground text-center leading-relaxed max-w-3xl mx-auto">
                            XP Lab was created by data professionals who've been on both sides of the hiring table.
                            We've seen talented people struggle to break in, and we've seen companies struggle to find
                            job-ready candidates. We built XP Lab to bridge that gap.
                        </p>
                    </div>
                </div>

                {/* Contact Section */}
                <div className="bg-gradient-to-r from-emerald-600/10 via-green-500/10 to-emerald-600/10 rounded-lg p-8 text-center space-y-4">
                    <h2 className="text-2xl font-bold text-foreground">Get in Touch</h2>
                    <p className="text-muted-foreground">
                        Have questions, feedback, or want to collaborate? We'd love to hear from you.
                    </p>
                    <div className="pt-2">
                        <a
                            href="mailto:tal@xp-lab.com"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-lg font-medium 
                       hover:bg-accent/90 transition-colors duration-200"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Contact Us
                        </a>
                    </div>
                </div>
            </div>
        </div>
    )
}
